"use client";

import { useSession } from "@packages/sdk";
import { useSearchParams } from "next/navigation";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";

import { useSearchParamState } from "../../../hooks";
import { Loader } from "../other";
import { AuthMagicFlow } from "./AuthMagicFlow";
import { AuthMainFlow } from "./AuthMainFlow";
import { AuthNamesFlow } from "./AuthNamesFlow";
import type {
  AuthFlow,
  AuthFlowProps,
  AuthMethod,
  AuthProps,
} from "./AuthTypes";
import { EmailAuthFlow } from "./EmailAuthFlow";
import { PasswordAuthFlow } from "./PasswordAuthFlow";
import { PasswordAuthFlowWithSignup } from "./PasswordAuthFlowWithSignup";
import { PhoneAuthFlow } from "./PhoneAuthFlow";
import { PhoneCodeAuthFlow } from "./PhoneCodeAuthFlow";

type AuthComponentProps = AuthProps & {
  onFlowChange?: (flow: AuthFlow) => void;
};

export const Auth = ({
  onFlowChange: handleFlowChange,
  allowedMethods = ["email", "phone", "apple", "google"],
  hideHeader,
  onAuth,
  ...props
}: AuthComponentProps) => {
  const initialFlow = props.email ? "email" : props.phone ? "phone" : "main";
  const [flow, setFlow] = useSearchParamState<false, AuthFlow>({
    defaultValue: initialFlow,
    key: "flow",
    removeFromUrlIfDefaultValue: false,
    multiple: false,
  });
  const [state, setState] = useState<Record<string, any>>({});
  const [initialCheck, setInitialCheck] = useState<boolean>(false);
  const { status } = useSession();
  const searchParams = useSearchParams();
  const [method, setMethod] = useState<AuthMethod>("email");
  const [isLoading, setIsLoading] = useState(false);

  const { login } = useSession({
    onAuthenticated: ({ isNewUser }) => {
      setIsLoading(true);
      if (flow !== "names" && isNewUser) setFlow("names");
      else onAuth({ isNewUser });
      setIsLoading(false);
    },
  });

  useEffect(() => {
    if (
      !initialCheck &&
      !searchParams.has("isAuthenticated") &&
      !searchParams.has("isNewUser")
    ) {
      if (status === "authenticated") onAuth?.({ isNewUser: false });
      if (status !== "loading") setInitialCheck(true);
    }
  }, [status, initialCheck]);

  useEffect(() => {
    const urlFlow = searchParams.get("flow");
    if (
      urlFlow !== flow &&
      ["email", "phone", "phone_code", "password", "main"].includes(urlFlow)
    ) {
      setFlow(urlFlow as AuthFlow);
      handleFlowChange?.(urlFlow as AuthFlow);
    }
  }, [searchParams]);

  /** setSessionListenerURLParams onAuth for forms  */
  const handleAuth: AuthFlowProps["onAuth"] = ({ data }) => {
    login({ isNewUser: data?.is_new, token: data?.oauth?.access_token });
  };

  const onFlowChange = (flow: AuthFlow) => {
    setFlow(flow);
    handleFlowChange?.(flow);
  };

  const flowProps = {
    ...props,
    onAuth: handleAuth,
    hideHeader,
    allowedMethods,
    flow,
    setFlow: onFlowChange,
    state,
    setState,
    method,
    setMethod,
  };

  const flows: Record<AuthFlow, ReactNode> = {
    main: <AuthMainFlow {...flowProps} />,
    email: <EmailAuthFlow {...flowProps} />,
    password: <PasswordAuthFlow {...flowProps} />,
    /** Allow Password signups IF NOT in current A/B magic-link treatment */
    passwordWithSignup: <PasswordAuthFlowWithSignup {...flowProps} />,
    phone: <PhoneAuthFlow {...flowProps} />,
    phone_code: <PhoneCodeAuthFlow {...flowProps} />,
    magic: <AuthMagicFlow {...flowProps} />,
    /** Use onAuth for "names" */
    names: <AuthNamesFlow {...{ ...flowProps, onAuth }} />,
  };

  if (isLoading) return <Loader />;

  return flows[flow];
};

Auth.displayName = "Auth";
